import React from 'react';
import {
    BarChart,
    LineChart,
    AreaChart,
    DonutChart,
} from '@tremor/react';
import type { ChartComponent } from '../../schemas/chart';
import { cn } from '../../utils/cn';

interface ChartRendererProps {
    component: ChartComponent;
    className?: string;
}

// Default color palette
const COLORS = [
    'blue',
    'emerald',
    'amber',
    'red',
    'violet',
    'cyan',
    'orange',
    'pink',
];

/**
 * Render a chart component using Tremor
 */
export function ChartRenderer({ component, className }: ChartRendererProps) {
    const { chart_type, title, labels, datasets, show_legend = true } = component;

    // Transform data for Tremor charts
    const chartData = labels.map((label, index) => {
        const dataPoint: Record<string, unknown> = { name: label };
        datasets.forEach((dataset) => {
            dataPoint[dataset.label] = dataset.data[index];
        });
        return dataPoint;
    });

    // Get category names (dataset labels)
    const categories = datasets.map((d) => d.label);

    // Get colors for datasets
    const colors = datasets.map((d, i) => {
        if (d.color) {
            // Map hex colors to Tremor color names if possible
            return mapToTremorColor(d.color) || COLORS[i % COLORS.length];
        }
        return COLORS[i % COLORS.length];
    });

    return (
        <div className={cn('w-full rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900', className)}>
            {title && (
                <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                </h3>
            )}
            <div className="h-64">
                {renderChart(chart_type, chartData, categories, colors, show_legend)}
            </div>
        </div>
    );
}

function renderChart(
    type: ChartComponent['chart_type'],
    data: Record<string, unknown>[],
    categories: string[],
    colors: string[],
    showLegend: boolean
): React.ReactElement {
    const commonProps = {
        data,
        index: 'name',
        categories,
        colors,
        showLegend,
        className: 'h-full',
    };

    switch (type) {
        case 'bar':
            return <BarChart {...commonProps} />;

        case 'line':
            return <LineChart {...commonProps} />;

        case 'area':
            return <AreaChart {...commonProps} />;

        case 'pie':
        case 'doughnut':
            // For pie/doughnut, use DonutChart
            // Transform data for donut chart
            const donutData = data.map((item) => ({
                name: item.name as string,
                value: item[categories[0]] as number,
            }));

            return (
                <DonutChart
                    data={donutData}
                    index="name"
                    category="value"
                    colors={colors}
                    showLabel
                    className="h-full"
                />
            );

        default:
            return <div className="flex h-full items-center justify-center text-gray-500">Unsupported chart type: {type}</div>;
    }
}

/**
 * Map hex color to Tremor color name
 */
function mapToTremorColor(hex: string): string | null {
    const colorMap: Record<string, string> = {
        '#3b82f6': 'blue',
        '#10b981': 'emerald',
        '#f59e0b': 'amber',
        '#ef4444': 'red',
        '#8b5cf6': 'violet',
        '#06b6d4': 'cyan',
        '#f97316': 'orange',
        '#ec4899': 'pink',
    };
    return colorMap[hex.toLowerCase()] || null;
}
